import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Save,
  Delete,
  Add,
  Remove,
  Edit,
  CloudUpload,
  Download,
  PictureAsPdf,
  Print,
  FileCopy,
  Preview,
  Business,
  Person,
  Receipt,
  Build,
  LocalShipping,
  Settings,
  Description,
  Template,
  ContentCopy,
  Visibility,
  AttachMoney,
  ShoppingCart,
  Title,
  TextFields,
  BorderColor,
  FormatSize,
  ColorLens,
  InsertDriveFile,
  CheckBox,
  ToggleOn,
  ToggleOff,
  Info,
  Help,Launch,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import { useNavigate } from 'react-router-dom';
const TemplateDesigner = () => {
  const navigate =useNavigate();
  // State for template data with PDF-like structure
  const [template, setTemplate] = useState({
    id: Date.now().toString(),
    name: 'Professional Quotation Template',
    version: '1.0',
    
    // Header Section
    header: {
      // Left side details
      company_details_left: [
        { id: 'cd1', label: 'Address:', content: '123 Business Street, City, State, ZIP', editable: true, visible: true },
        { id: 'cd2', label: 'Email:', content: 'info@company.com', editable: true, visible: true },
        { id: 'cd3', label: 'Contact:', content: '+91 9876543210', editable: true, visible: true },
        { id: 'cd4', label: 'GST/VAT:', content: 'GSTIN123456789', editable: true, visible: true },
      ],
      // Right side company name
      company_name_right: 'YOUR COMPANY NAME',
      company_name_size: 18,
      company_name_bold: true,
      
      // Title
      title: 'QUOTATION',
      title_size: 16,
      title_underline: true,
      
      // Styling
      header_color: '#1976d2',
      text_color_white: true,
    },
    
    // Bill To & Quotation Details Section
    details_section: {
      // Left side: Bill To
      bill_to: {
        title: 'Bill To:',
        client_name: 'Client Company Name',
        client_address: 'Client Address Here',
        client_contact: 'Contact: +91 9876543210',
        client_email: 'Email: client@example.com',
        client_gst: 'GST/VAT: CLIENTGST123456',
        show_attention: false,
        attention: 'Attention: Accounts Department',
      },
      
      // Right side: Quotation Details
      quotation_details: {
        title: 'Quotation Details',
        rows: [
          { id: 'qd1', label: 'Quotation #:', content: 'QT2024001', placeholder: 'Auto-generate' },
          { id: 'qd2', label: 'Date:', content: new Date().toISOString().split('T')[0], placeholder: '' },
          { id: 'qd3', label: 'Valid Until:', content: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().split('T')[0], placeholder: '' },
          { id: 'qd4', label: 'Currency:', content: 'INR', placeholder: '' },
        ],
      },
      
      layout: {
        side_by_side: true,
        spacing: 30, // mm between sections
      },
    },
    
    // Scope of Work Section
    scope_section: {
      enabled: true,
      title: 'Scope of Work:',
      content: 'Professional repair and retrofitting of industrial electronic boards including diagnostics, component replacement, testing, and commissioning.',
      placeholder: 'Brief description of repair/retrofitting work...',
    },
    
    // Items Table Section
    items_table: {
      enabled: true,
      title: 'Items & Services',
      columns: [
        { id: 'col1', name: '#', width: 5, visible: true },
        { id: 'col2', name: 'Description', width: 30, visible: true },
        { id: 'col3', name: 'Unit', width: 10, visible: true },
        { id: 'col4', name: 'Unit Price', width: 15, visible: true },
        { id: 'col5', name: 'Qty', width: 10, visible: true },
        { id: 'col6', name: 'Subtotal', width: 10, visible: true },
        { id: 'col7', name: 'CGST%', width: 7.5, visible: true },
        { id: 'col8', name: 'SGST%', width: 7.5, visible: true },
        { id: 'col9', name: 'Amt (inc. GST)', width: 15, visible: true },
        { id: 'col10', name: 'Remarks', width: 10, visible: false },
      ],
      rows: [
        {
          id: 'row1',
          description: 'Electronic Board Repair',
          unit: 'pc',
          unit_price: 0,
          default_qty: 1,
          cgst: 9,
          sgst: 9,
          remarks: '',
          required: false,
        },
        {
          id: 'row2',
          description: 'Component Replacement',
          unit: 'pc',
          unit_price: 0,
          default_qty: 1,
          cgst: 9,
          sgst: 9,
          remarks: '',
          required: false,
        },
      ],
      show_header_color: true,
      header_color: '#1976d2',
    },
    
    // Additional Costs Section
    additional_costs: {
      enabled: true,
      title: 'Additional Costs:',
      costs: [
        {
          id: 'ac1',
          name: 'Diagnostics/Evaluation Fee',
          default_value: 0,
          enabled_by_default: true,
          description: 'Initial evaluation and testing fee',
          required: false,
        },
        {
          id: 'ac2',
          name: 'Labour & Repair Cost',
          default_value: 0,
          enabled_by_default: true,
          description: 'Technical labor charges',
          required: false,
        },
        {
          id: 'ac3',
          name: 'Parts Estimate',
          default_value: 0,
          enabled_by_default: true,
          description: 'Estimated cost of replacement parts',
          required: false,
        },
        {
          id: 'ac4',
          name: 'Testing & Commissioning',
          default_value: 0,
          enabled_by_default: true,
          description: 'Final testing and commissioning charges',
          required: false,
        },
        {
          id: 'ac5',
          name: 'Packaging & Shipping',
          default_value: 0,
          enabled_by_default: false,
          description: 'Packaging and shipping charges',
          required: false,
        },
      ],
      show_as_table: true,
      table_columns: [
        { id: 'acc1', name: 'Cost Description', width: 60 },
        { id: 'acc2', name: 'Amount', width: 40 },
      ],
    },
    
  // Summary Section
summary_section: {
  enabled: true,
  title: 'Quotation Summary',
  show_items_total: true,
  show_additional_costs: true,
  show_subtotal: true,
  show_discount: true,
  show_cgst_sgst_separate: true,
  show_total_gst: true,
  show_grand_total: true,
  default_discount_percent: 0,
  show_notes_box: true,
  notes_content: 'Important Notes:\n• Quotation valid for 30 days\n• Warranty: 3 months\n• Lead time: 7 days',
  layout: 'two_column',
  
  // ADVANCE PAYMENT SECTION INSIDE summary_section
  show_advance_payment: true,
  advance_payment_options: {
    full_payment: {
      enabled: true,
      label: 'Full Payment',
      description: '100% payment upon order confirmation'
    },
    advance_payment: {
      enabled: true,
      label: 'Advance Payment',
      percentage: 50,
      description: 'Advance payment upon order confirmation'
    },
    custom_payment: {
      enabled: false,
      label: 'Custom Payment Terms',
      description: 'Custom payment terms as agreed'
    }
  },
  show_bank_details: true,
  bank_details: {
    bank_name: 'ABC Bank',
    account_number: '1234567890',
    account_name: 'Your Company Name',
    ifsc_code: 'ABC0123456',
    branch: 'Main Branch, City'
  }
},
    
    // Terms & Conditions Section
    terms_section: {
      enabled: true,
      title: 'Terms & Conditions:',
      items: [
        {
          id: 't1',
          label: 'Warranty:',
          content: '3 months standard warranty on repaired components',
          visible: true,
        },
        {
          id: 't2',
          label: 'Lead Time:',
          content: '7 days',
          visible: true,
        },
        {
          id: 't3',
          label: 'Payment Terms:',
          content: '50% advance payment upon order confirmation, 50% upon completion before delivery',
          visible: true,
        },
        {
          id: 't4',
          label: 'Delivery Terms:',
          content: 'Ex-works (FOB Origin). Shipping costs borne by client.',
          visible: true,
        },
        {
          id: 't5',
          label: 'Notes:',
          content: 'This quotation is valid for 30 days. Prices are subject to change without notice.',
          visible: true,
        },
      ],
    },
    
    // Footer
    footer: {
      enabled: true,
      content: 'Thank you for your business!',
      show_page_numbers: true,
    },
    
    // General settings
    settings: {
      currency: 'INR',
      currency_symbol: '₹',
      page_size: 'A4',
      orientation: 'portrait',
      margins: {
        top: 20,
        bottom: 20,
        left: 10,
        right: 10,
      },
      font_family: 'helvetica',
      default_font_size: 10,
    },
  });

  const [activeSection, setActiveSection] = useState('header');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        if (company_id) {
          // Import axios if needed, but fetch works too
          const cmpRes = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/cmp/${company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => res.json());
          if (cmpRes.data?.currency_symbol) {
            setTemplate(prev => ({
              ...prev,
              settings: {
                ...prev.settings,
                currency_symbol: cmpRes.data.currency_symbol
              }
            }));
          }
        }
      } catch (e) {}
    };
    fetchConfig();
  }, []);

  // Calculate preview totals
  const calculatePreviewTotals = () => {
    let itemsTotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    
    template.items_table.rows.forEach(item => {
      const itemSubtotal = (item.default_qty || 1) * (item.unit_price || 0);
      itemsTotal += itemSubtotal;
      
      const itemCGST = itemSubtotal * ((item.cgst || 0) / 100);
      const itemSGST = itemSubtotal * ((item.sgst || 0) / 100);
      cgstTotal += itemCGST;
      sgstTotal += itemSGST;
    });
    
    const additionalCosts = template.additional_costs.costs
      .filter(cost => cost.enabled_by_default)
      .reduce((sum, cost) => sum + (parseFloat(cost.default_value) || 0), 0);
    
    const subtotal = itemsTotal + additionalCosts;
    const discountAmount = subtotal * (template.summary_section.default_discount_percent / 100);
    const afterDiscount = subtotal - discountAmount;
    
    const gstTotal = cgstTotal + sgstTotal;
    const grandTotal = afterDiscount + gstTotal;
    
    return { 
      itemsTotal, 
      subtotal, 
      discountAmount, 
      afterDiscount,
      cgstTotal,
      sgstTotal,
      gstTotal,
      grandTotal,
      additionalCosts
    };
  };

  const previewTotals = calculatePreviewTotals();

  // Format currency
  const formatCurrency = (amount) => {
    const symbol = template.settings.currency_symbol || '₹';
    return `${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handler functions
  const handleSaveTemplate = () => {
    const templates = JSON.parse(localStorage.getItem('quotation_templates') || '[]');
    const existingIndex = templates.findIndex(t => t.id === template.id);
    
    if (existingIndex >= 0) {
      templates[existingIndex] = template;
    } else {
      templates.push(template);
    }
    
    localStorage.setItem('quotation_templates', JSON.stringify(templates));
    
    setSnackbar({
      open: true,
      message: 'Template saved successfully!',
      severity: 'success'
    });
  };

  const handleUseTemplate = () => {
    // Save template to localStorage with additional metadata
    const templateToSave = {
      ...template,
      savedAt: new Date().toISOString(),
      id: template.id || Date.now().toString(),
      // Add any additional metadata needed
    };
    
    localStorage.setItem('currentTemplate', JSON.stringify(templateToSave));
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'Template saved! Redirecting to quotation editor...',
      severity: 'success'
    });
    
    // Navigate after a short delay to show the message
    setTimeout(() => {
      navigate('/quotation-from-template');
    }, 1000);
  };

  const handleAddTableRow = () => {
    const newRow = {
      id: `row_${Date.now()}`,
      description: 'New Item',
      unit: 'pc',
      unit_price: 0,
      default_qty: 1,
      cgst: 9,
      sgst: 9,
      remarks: '',
      required: false,
    };
    
    setTemplate(prev => ({
      ...prev,
      items_table: {
        ...prev.items_table,
        rows: [...prev.items_table.rows, newRow]
      }
    }));
  };

  const handleRemoveTableRow = (index) => {
    setTemplate(prev => ({
      ...prev,
      items_table: {
        ...prev.items_table,
        rows: prev.items_table.rows.filter((_, i) => i !== index)
      }
    }));
  };

  const handleUpdateTableRow = (index, field, value) => {
    const updatedRows = [...template.items_table.rows];
    updatedRows[index] = { ...updatedRows[index], [field]: value };
    
    setTemplate(prev => ({
      ...prev,
      items_table: {
        ...prev.items_table,
        rows: updatedRows
      }
    }));
  };

  const handleAddAdditionalCost = () => {
    const newCost = {
      id: `ac_${Date.now()}`,
      name: 'New Additional Cost',
      default_value: 0,
      enabled_by_default: true,
      description: '',
      required: false,
    };
    
    setTemplate(prev => ({
      ...prev,
      additional_costs: {
        ...prev.additional_costs,
        costs: [...prev.additional_costs.costs, newCost]
      }
    }));
  };

  const handleRemoveAdditionalCost = (index) => {
    setTemplate(prev => ({
      ...prev,
      additional_costs: {
        ...prev.additional_costs,
        costs: prev.additional_costs.costs.filter((_, i) => i !== index)
      }
    }));
  };

  const handleUpdateAdditionalCost = (index, field, value) => {
    const updatedCosts = [...template.additional_costs.costs];
    updatedCosts[index] = { ...updatedCosts[index], [field]: value };
    
    setTemplate(prev => ({
      ...prev,
      additional_costs: {
        ...prev.additional_costs,
        costs: updatedCosts
      }
    }));
  };

  const handleAddTerm = () => {
    const newTerm = {
      id: `t_${Date.now()}`,
      label: 'New Term:',
      content: 'Term content here',
      visible: true,
    };
    
    setTemplate(prev => ({
      ...prev,
      terms_section: {
        ...prev.terms_section,
        items: [...prev.terms_section.items, newTerm]
      }
    }));
  };

  const handleRemoveTerm = (index) => {
    setTemplate(prev => ({
      ...prev,
      terms_section: {
        ...prev.terms_section,
        items: prev.terms_section.items.filter((_, i) => i !== index)
      }
    }));
  };

  const handleUpdateTerm = (index, field, value) => {
    const updatedTerms = [...template.terms_section.items];
    updatedTerms[index] = { ...updatedTerms[index], [field]: value };
    
    setTemplate(prev => ({
      ...prev,
      terms_section: {
        ...prev.terms_section,
        items: updatedTerms
      }
    }));
  };

  // Generate PDF preview
const generatePDFPreview = () => {
  const pdf = new jsPDF({
    orientation: template.settings.orientation,
    unit: 'mm',
    format: template.settings.page_size,
  });

  // Helper functions
  const formatCurrency = (amount) => {
    const symbol = template.settings.currency_symbol || '₹';
    return `${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const addText = (text, x, y, options = {}) => {
    if (!text) text = '';
    // Clean text to remove special characters that cause issues
    const cleanText = String(text)
      .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
      .trim();
    
    try {
      pdf.text(cleanText, x, y, options);
    } catch (err) {
      //console.error('Error adding text:', err);
      // Fallback: try with empty string
      pdf.text('', x, y, options);
    }
  };

  // Page dimensions
  const pageWidth = 210;
  const pageHeight = 297;
  let yPos = template.settings.margins.top;

  // ==================== HEADER SECTION ====================
  pdf.setFillColor(template.header.header_color);
  pdf.rect(
    template.settings.margins.left,
    yPos,
    190,
    25,
    'F'
  );

  pdf.setTextColor(255, 255, 255);
  
  // Left side details
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  let leftY = yPos + 10;
  
  template.header.company_details_left
    .filter(detail => detail.visible)
    .forEach(detail => {
      if (detail.content) {
        addText(`${detail.label} ${detail.content}`, template.settings.margins.left + 5, leftY);
        leftY += 5;
      }
    });

  // Right-aligned company name
  pdf.setFontSize(template.header.company_name_size);
  pdf.setFont('helvetica', template.header.company_name_bold ? 'bold' : 'normal');
  
  // Clean company name for PDF
  const cleanCompanyName = template.header.company_name_right
    .replace(/[^\x00-\x7F]/g, '')
    .trim();
  
  addText(
    cleanCompanyName,
    pageWidth - template.settings.margins.right,
    yPos + 15,
    { align: 'right' }
  );

  // Quotation title
  yPos += 35;
  pdf.setFontSize(template.header.title_size);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  
  addText(template.header.title, pageWidth / 2, yPos, { align: 'center' });
  
  if (template.header.title_underline) {
    const titleWidth = pdf.getStringUnitWidth(template.header.title) * template.header.title_size / pdf.internal.scaleFactor;
    const underlineStartX = (pageWidth / 2) - (titleWidth / 2);
    pdf.line(underlineStartX, yPos + 2, underlineStartX + titleWidth, yPos + 2);
  }

  yPos += 10;

  // ==================== BILL TO & QUOTATION DETAILS ====================
  const detailsStartY = yPos;
  
  // Left side: Bill To
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  addText('Bill To:', template.settings.margins.left, detailsStartY);
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  let billY = detailsStartY + 7;
  
  const billToLines = [
    template.details_section.bill_to.client_name,
    template.details_section.bill_to.client_address,
    template.details_section.bill_to.client_contact,
    template.details_section.bill_to.client_email,
    template.details_section.bill_to.client_gst,
  ];
  
  if (template.details_section.bill_to.show_attention) {
    billToLines.push(template.details_section.bill_to.attention);
  }
  
  billToLines.forEach((line, index) => {
    if (line && line.trim()) {
      addText(line, template.settings.margins.left + 5, billY + (index * 5));
    }
  });

  // Right side: Quotation Details
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  addText('Quotation Details', 110, detailsStartY);
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  let detailsY = detailsStartY + 7;
  
  template.details_section.quotation_details.rows.forEach((row, index) => {
    if (row.label && (row.content || row.placeholder)) {
      // Clean the text before adding
      const cleanLabel = String(row.label).replace(/[^\x00-\x7F]/g, '').trim();
      const cleanContent = String(row.content || row.placeholder).replace(/[^\x00-\x7F]/g, '').trim();
      
      addText(cleanLabel, 115, detailsY + (index * 5));
      addText(cleanContent, 150, detailsY + (index * 5));
    }
  });

  // Update yPos
  const leftHeight = billToLines.filter(l => l && l.trim()).length * 5 + 15;
  const rightHeight = template.details_section.quotation_details.rows.length * 5 + 15;
  yPos = detailsStartY + Math.max(leftHeight, rightHeight) + 10;

  // ==================== ITEMS TABLE ====================
  if (template.items_table.enabled && template.items_table.rows.length > 0) {
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    addText(template.items_table.title, template.settings.margins.left, yPos);
    yPos += 6;

    // Table Headers - Only show visible columns
    const visibleColumns = template.items_table.columns.filter(col => col.visible);
    const headers = visibleColumns.map(col => col.name);
    
    // Calculate column widths in mm (total width 190mm)
    const totalPercentage = visibleColumns.reduce((sum, col) => sum + col.width, 0);
    const colWidths = visibleColumns.map(col => (col.width / totalPercentage) * 190);

    // Draw header background
    if (template.items_table.show_header_color) {
      pdf.setFillColor(template.items_table.header_color || template.header.header_color);
    } else {
      pdf.setFillColor(41, 128, 185);
    }
    
    pdf.rect(template.settings.margins.left, yPos, 190, 8, 'F');
    
    // Header text
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    let headerX = template.settings.margins.left;
    
    headers.forEach((header, index) => {
      const colCenter = headerX + (colWidths[index] / 2);
      addText(header, colCenter, yPos + 6, { align: 'center' });
      headerX += colWidths[index];
    });
    
    pdf.setTextColor(0, 0, 0);
    yPos += 8;

    // Table Rows
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    template.items_table.rows.forEach((item, index) => {
      // Check page break
      if (yPos > pageHeight - 50) {
        pdf.addPage();
        yPos = template.settings.margins.top;
      }
      
      let rowX = template.settings.margins.left;
      const itemSubtotal = (item.default_qty || 1) * (item.unit_price || 0);
      const cgstAmount = itemSubtotal * ((item.cgst || 0) / 100);
      const sgstAmount = itemSubtotal * ((item.sgst || 0) / 100);
      const amountWithGST = itemSubtotal + cgstAmount + sgstAmount;
      
      // Calculate row height based on description
      const cleanDescription = String(item.description || '').replace(/[^\x00-\x7F]/g, '').trim();
      const descriptionLines = pdf.splitTextToSize(cleanDescription, colWidths[1] - 4);
      const rowHeight = Math.max(descriptionLines.length * 5, 8);
      
      // Draw row border
      visibleColumns.forEach((col, colIndex) => {
        pdf.rect(rowX, yPos, colWidths[colIndex], rowHeight);
        rowX += colWidths[colIndex];
      });
      
      // Reset rowX for text
      rowX = template.settings.margins.left;
      
      // Add cell content
      visibleColumns.forEach((col, colIndex) => {
        let cellText = '';
        let align = 'left';
        
        switch(col.name) {
          case '#':
            cellText = String(index + 1);
            align = 'center';
            break;
          case 'Description':
            // Handle multi-line description
            descriptionLines.forEach((line, lineIndex) => {
              addText(line, rowX + 2, yPos + 5 + (lineIndex * 5));
            });
            break;
          case 'Unit':
            cellText = String(item.unit || '').replace(/[^\x00-\x7F]/g, '').trim();
            align = 'center';
            break;
          case 'Unit Price':
            cellText = formatCurrency(item.unit_price || 0);
            align = 'right';
            break;
          case 'Qty':
            cellText = String(item.default_qty || 1);
            align = 'center';
            break;
          case 'Subtotal':
            cellText = formatCurrency(itemSubtotal);
            align = 'right';
            break;
          case 'CGST%':
            cellText = `${item.cgst || 0}%`;
            align = 'right';
            break;
          case 'SGST%':
            cellText = `${item.sgst || 0}%`;
            align = 'right';
            break;
          case 'Amt (inc. GST)':
            cellText = formatCurrency(amountWithGST);
            align = 'right';
            break;
          case 'Remarks':
            cellText = String(item.remarks || '').replace(/[^\x00-\x7F]/g, '').trim();
            align = 'left';
            break;
        }
        
        // Add text if not description (already handled)
        if (col.name !== 'Description' && cellText) {
          let textX = rowX + 2;
          if (align === 'center') {
            textX = rowX + (colWidths[colIndex] / 2);
          } else if (align === 'right') {
            textX = rowX + colWidths[colIndex] - 2;
          }
          
          addText(cellText, textX, yPos + 5, { align: align });
        }
        
        rowX += colWidths[colIndex];
      });
      
      yPos += rowHeight;
    });
    
    yPos += 10;
  }

  // ==================== ADDITIONAL COSTS ====================
  if (template.additional_costs.enabled) {
    const enabledCosts = template.additional_costs.costs.filter(cost => cost.enabled_by_default);
    
    if (enabledCosts.length > 0) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      addText(template.additional_costs.title, template.settings.margins.left, yPos);
      yPos += 5;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      if (template.additional_costs.show_as_table) {
        // Table layout
        const costLabelWidth = 120;
        const costValueWidth = 70;
        
        enabledCosts.forEach((cost, index) => {
          // Check page break
          if (yPos > pageHeight - 20) {
            pdf.addPage();
            yPos = template.settings.margins.top;
          }
          
          pdf.rect(template.settings.margins.left, yPos, costLabelWidth, 8);
          pdf.rect(template.settings.margins.left + costLabelWidth, yPos, costValueWidth, 8);
          
          addText(cost.name, template.settings.margins.left + 5, yPos + 5);
          addText(formatCurrency(cost.default_value || 0), 
                  template.settings.margins.left + costLabelWidth + costValueWidth - 5, 
                  yPos + 5, 
                  { align: 'right' });
          
          yPos += 8;
        });
      } else {
        // List layout
        enabledCosts.forEach((cost, index) => {
          addText(`${cost.name}: ${formatCurrency(cost.default_value || 0)}`, 
                  template.settings.margins.left + 5, 
                  yPos + (index * 5));
        });
        yPos += (enabledCosts.length * 5);
      }
      
      yPos += 10;
    }
  }

  // ==================== SUMMARY SECTION ====================
  if (template.summary_section.enabled) {
    // Calculate totals
    let itemsTotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    
    template.items_table.rows.forEach(item => {
      const itemSubtotal = (item.default_qty || 1) * (item.unit_price || 0);
      itemsTotal += itemSubtotal;
      
      const itemCGST = itemSubtotal * ((item.cgst || 0) / 100);
      const itemSGST = itemSubtotal * ((item.sgst || 0) / 100);
      cgstTotal += itemCGST;
      sgstTotal += itemSGST;
    });
    
    const additionalCosts = template.additional_costs.costs
      .filter(cost => cost.enabled_by_default)
      .reduce((sum, cost) => sum + (parseFloat(cost.default_value) || 0), 0);
    
    const subtotal = itemsTotal + additionalCosts;
    const discountAmount = subtotal * (template.summary_section.default_discount_percent / 100);
    const afterDiscount = subtotal - discountAmount;
    const gstTotal = cgstTotal + sgstTotal;
    const grandTotal = afterDiscount + gstTotal;
    
    // Check page break for summary
    if (yPos > pageHeight - 100) {
      pdf.addPage();
      yPos = template.settings.margins.top;
    }
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    addText(template.summary_section.title, template.settings.margins.left, yPos);
    yPos += 6;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Summary table
    const summaryLabelWidth = 120;
    const summaryValueWidth = 70;
    
    // Build summary rows
    const summaryRows = [];
    
    if (template.summary_section.show_items_total) {
      summaryRows.push(['Items Total:', formatCurrency(itemsTotal)]);
    }
    
    if (template.summary_section.show_additional_costs) {
      summaryRows.push(['Additional Costs:', formatCurrency(additionalCosts)]);
    }
    
    if (template.summary_section.show_subtotal) {
      summaryRows.push(['Subtotal:', formatCurrency(subtotal)]);
    }
    
    if (template.summary_section.show_discount && template.summary_section.default_discount_percent > 0) {
      summaryRows.push([`Discount (${template.summary_section.default_discount_percent}%):`, `-${formatCurrency(discountAmount)}`]);
      summaryRows.push(['After Discount:', formatCurrency(afterDiscount)]);
    }
    
    if (template.summary_section.show_cgst_sgst_separate) {
      summaryRows.push(['Total CGST:', formatCurrency(cgstTotal)]);
      summaryRows.push(['Total SGST:', formatCurrency(sgstTotal)]);
    }
    
    if (template.summary_section.show_total_gst) {
      summaryRows.push(['Total GST:', formatCurrency(gstTotal)]);
    }
    
    if (template.summary_section.show_grand_total) {
      summaryRows.push(['GRAND TOTAL:', formatCurrency(grandTotal)]);
    }
    
    // Draw summary table
    pdf.rect(template.settings.margins.left, yPos, summaryLabelWidth + summaryValueWidth, summaryRows.length * 8);
    
    summaryRows.forEach(([label, value], index) => {
      const rowY = yPos + (index * 8);
      
      pdf.rect(template.settings.margins.left, rowY, summaryLabelWidth, 8);
      pdf.rect(template.settings.margins.left + summaryLabelWidth, rowY, summaryValueWidth, 8);
      
      if (label === 'GRAND TOTAL:') {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(41, 128, 185);
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
      }
      
      // Clean label text
      const cleanLabel = String(label).replace(/[^\x00-\x7F]/g, '').trim();
      addText(cleanLabel, template.settings.margins.left + 5, rowY + 5);
      
      // Clean value text
      const cleanValue = String(value).replace(/[^\x00-\x7F]/g, '').trim();
      addText(cleanValue, template.settings.margins.left + summaryLabelWidth + summaryValueWidth - 5, rowY + 5, { align: 'right' });
    });
    
    yPos += summaryRows.length * 8 + 15;
  }

  // ==================== TERMS & CONDITIONS ====================
  if (template.terms_section.enabled) {
    // Check page break
    if (yPos > pageHeight - 50) {
      pdf.addPage();
      yPos = template.settings.margins.top;
    }
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    addText(template.terms_section.title, template.settings.margins.left, yPos);
    yPos += 5;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    template.terms_section.items
      .filter(term => term.visible)
      .forEach((term, index) => {
        // Clean term text
        const cleanLabel = String(term.label).replace(/[^\x00-\x7F]/g, '').trim();
        const cleanContent = String(term.content).replace(/[^\x00-\x7F]/g, '').trim();
        
        addText(cleanLabel, template.settings.margins.left + 5, yPos + (index * 5));
        
        const contentLines = pdf.splitTextToSize(cleanContent, 130);
        contentLines.forEach((line, lineIndex) => {
          addText(line, 60, yPos + (index * 5) + (lineIndex * 5));
        });
        
        yPos += (contentLines.length * 5);
      });
    
    yPos += 10;
  }

  // Save PDF
  pdf.save(`${template.name}_preview.pdf`);
};

  // Render template sections
  const renderTemplateSection = () => {
    switch (activeSection) {
      case 'header':
        return renderHeaderSection();
      case 'details':
        return renderDetailsSection();
      case 'items':
        return renderItemsSection();
      case 'costs':
        return renderCostsSection();
      case 'summary':
        return renderSummarySection();
      case 'terms':
        return renderTermsSection();
      default:
        return renderHeaderSection();
    }
  };

  const renderHeaderSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Title /> Header Section
      </Typography>
      
      <Grid container spacing={3}>
        {/* Header Color */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Header Color"
            type="color"
            value={template.header.header_color}
            onChange={(e) => setTemplate(prev => ({
              ...prev,
              header: { ...prev.header, header_color: e.target.value }
            }))}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ColorLens />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Company Name */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Company Name (Right Side)</Typography>
          <TextField
            fullWidth
            value={template.header.company_name_right}
            onChange={(e) => setTemplate(prev => ({
              ...prev,
              header: { ...prev.header, company_name_right: e.target.value }
            }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Font Size">
                      <Select
                        size="small"
                        value={template.header.company_name_size}
                        onChange={(e) => setTemplate(prev => ({
                          ...prev,
                          header: { ...prev.header, company_name_size: e.target.value }
                        }))}
                        sx={{ width: 80 }}
                      >
                        <MenuItem value={14}>14</MenuItem>
                        <MenuItem value={16}>16</MenuItem>
                        <MenuItem value={18}>18</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={22}>22</MenuItem>
                      </Select>
                    </Tooltip>
                    <Tooltip title="Bold">
                      <IconButton
                        size="small"
                        onClick={() => setTemplate(prev => ({
                          ...prev,
                          header: { ...prev.header, company_name_bold: !prev.header.company_name_bold }
                        }))}
                        color={template.header.company_name_bold ? "primary" : "default"}
                      >
                        <FormatSize />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Left Side Details */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Left Side Company Details</Typography>
          {template.header.company_details_left.map((detail, index) => (
            <Box key={detail.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField
                label="Label"
                size="small"
                value={detail.label}
                onChange={(e) => {
                  const updatedDetails = [...template.header.company_details_left];
                  updatedDetails[index].label = e.target.value;
                  setTemplate(prev => ({
                    ...prev,
                    header: { ...prev.header, company_details_left: updatedDetails }
                  }));
                }}
                sx={{ width: 120 }}
              />
              <TextField
                label="Content"
                size="small"
                fullWidth
                value={detail.content}
                onChange={(e) => {
                  const updatedDetails = [...template.header.company_details_left];
                  updatedDetails[index].content = e.target.value;
                  setTemplate(prev => ({
                    ...prev,
                    header: { ...prev.header, company_details_left: updatedDetails }
                  }));
                }}
              />
              <Switch
                size="small"
                checked={detail.visible}
                onChange={(e) => {
                  const updatedDetails = [...template.header.company_details_left];
                  updatedDetails[index].visible = e.target.checked;
                  setTemplate(prev => ({
                    ...prev,
                    header: { ...prev.header, company_details_left: updatedDetails }
                  }));
                }}
              />
              
              <IconButton
                size="small"
                onClick={() => {
                  const updatedDetails = template.header.company_details_left.filter((_, i) => i !== index);
                  setTemplate(prev => ({
                    ...prev,
                    header: { ...prev.header, company_details_left: updatedDetails }
                  }));
                }}
                color="error"
              >
                <Remove />
              </IconButton>
            </Box>
          ))}
          
          <Button
            startIcon={<Add />}
            onClick={() => {
              const newDetail = {
                id: `cd_${Date.now()}`,
                label: 'New Label:',
                content: 'New content',
                editable: true,
                visible: true,
              };
              setTemplate(prev => ({
                ...prev,
                header: {
                  ...prev.header,
                  company_details_left: [...prev.header.company_details_left, newDetail]
                }
              }));
            }}
            sx={{ mt: 1 }}
          >
            Add Detail
          </Button>
        </Grid>

        {/* Title */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Quotation Title</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              value={template.header.title}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                header: { ...prev.header, title: e.target.value }
              }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={template.header.title_underline}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    header: { ...prev.header, title_underline: e.target.checked }
                  }))}
                />
              }
              label="Underline"
            />
            <Select
              size="small"
              value={template.header.title_size}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                header: { ...prev.header, title_size: e.target.value }
              }))}
              sx={{ width: 80 }}
            >
              <MenuItem value={14}>14</MenuItem>
              <MenuItem value={16}>16</MenuItem>
              <MenuItem value={18}>18</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderDetailsSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Receipt /> Bill To & Quotation Details
      </Typography>
      
      <Grid container spacing={3}>
        {/* Bill To Section */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Bill To (Left Side)</Typography>
            
            <TextField
              fullWidth
              label="Client Name"
              value={template.details_section.bill_to.client_name}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                details_section: {
                  ...prev.details_section,
                  bill_to: { ...prev.details_section.bill_to, client_name: e.target.value }
                }
              }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Client Address"
              multiline
              rows={2}
              value={template.details_section.bill_to.client_address}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                details_section: {
                  ...prev.details_section,
                  bill_to: { ...prev.details_section.bill_to, client_address: e.target.value }
                }
              }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Contact"
              value={template.details_section.bill_to.client_contact}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                details_section: {
                  ...prev.details_section,
                  bill_to: { ...prev.details_section.bill_to, client_contact: e.target.value }
                }
              }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Email"
              value={template.details_section.bill_to.client_email}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                details_section: {
                  ...prev.details_section,
                  bill_to: { ...prev.details_section.bill_to, client_email: e.target.value }
                }
              }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="GST/VAT"
              value={template.details_section.bill_to.client_gst}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                details_section: {
                  ...prev.details_section,
                  bill_to: { ...prev.details_section.bill_to, client_gst: e.target.value }
                }
              }))}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.details_section.bill_to.show_attention}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    details_section: {
                      ...prev.details_section,
                      bill_to: { ...prev.details_section.bill_to, show_attention: e.target.checked }
                    }
                  }))}
                />
              }
              label="Show Attention Line"
              sx={{ mt: 2 }}
            />
            
            {template.details_section.bill_to.show_attention && (
              <TextField
                fullWidth
                label="Attention"
                value={template.details_section.bill_to.attention}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  details_section: {
                    ...prev.details_section,
                    bill_to: { ...prev.details_section.bill_to, attention: e.target.value }
                  }
                }))}
                sx={{ mt: 2 }}
              />
            )}
          </Paper>
        </Grid>

        {/* Quotation Details */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Quotation Details (Right Side)</Typography>
            
            {template.details_section.quotation_details.rows.map((row, index) => (
              <Box key={row.id} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <TextField
                  label="Label"
                  size="small"
                  value={row.label}
                  onChange={(e) => {
                    const updatedRows = [...template.details_section.quotation_details.rows];
                    updatedRows[index].label = e.target.value;
                    setTemplate(prev => ({
                      ...prev,
                      details_section: {
                        ...prev.details_section,
                        quotation_details: {
                          ...prev.details_section.quotation_details,
                          rows: updatedRows
                        }
                      }
                    }));
                  }}
                  sx={{ width: 150 }}
                />
                <TextField
                  label="Content"
                  size="small"
                  fullWidth
                  value={row.content}
                  onChange={(e) => {
                    const updatedRows = [...template.details_section.quotation_details.rows];
                    updatedRows[index].content = e.target.value;
                    setTemplate(prev => ({
                      ...prev,
                      details_section: {
                        ...prev.details_section,
                        quotation_details: {
                          ...prev.details_section.quotation_details,
                          rows: updatedRows
                        }
                      }
                    }));
                  }}
                  placeholder={row.placeholder}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedRows = template.details_section.quotation_details.rows.filter((_, i) => i !== index);
                    setTemplate(prev => ({
                      ...prev,
                      details_section: {
                        ...prev.details_section,
                        quotation_details: {
                          ...prev.details_section.quotation_details,
                          rows: updatedRows
                        }
                      }
                    }));
                  }}
                  color="error"
                >
                  <Remove />
                </IconButton>
              </Box>
            ))}
            
            <Button
              startIcon={<Add />}
              onClick={() => {
                const newRow = {
                  id: `qd_${Date.now()}`,
                  label: 'New Field:',
                  content: '',
                  placeholder: 'Enter value',
                };
                setTemplate(prev => ({
                  ...prev,
                  details_section: {
                    ...prev.details_section,
                    quotation_details: {
                      ...prev.details_section.quotation_details,
                      rows: [...prev.details_section.quotation_details.rows, newRow]
                    }
                  }
                }));
              }}
              sx={{ mt: 1 }}
            >
              Add Detail Field
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderItemsSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShoppingCart /> Items & Services Table
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={template.items_table.enabled}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                items_table: { ...prev.items_table, enabled: e.target.checked }
              }))}
            />
          }
          label="Enable Items Table"
        />
      </Box>
      
      {template.items_table.enabled && (
        <>
          {/* Table Columns */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Table Columns</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {template.items_table.columns.map((col, index) => (
                <Chip
                  key={col.id}
                  label={`${col.name} (${col.width}%)`}
                  onDelete={() => {
                    const updatedCols = template.items_table.columns.filter((_, i) => i !== index);
                    setTemplate(prev => ({
                      ...prev,
                      items_table: { ...prev.items_table, columns: updatedCols }
                    }));
                  }}
                  color={col.visible ? "primary" : "default"}
                  onClick={() => {
                    const updatedCols = [...template.items_table.columns];
                    updatedCols[index].visible = !updatedCols[index].visible;
                    setTemplate(prev => ({
                      ...prev,
                      items_table: { ...prev.items_table, columns: updatedCols }
                    }));
                  }}
                />
              ))}
            </Box>
          </Box>
          
          {/* Table Rows */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell width="5%">#</TableCell>
                  <TableCell width="30%">Description</TableCell>
                  <TableCell width="10%" align="center">Unit</TableCell>
                  <TableCell width="15%" align="right">Unit Price</TableCell>
                  <TableCell width="10%" align="center">Qty</TableCell>
                  <TableCell width="10%" align="center">CGST%</TableCell>
                  <TableCell width="10%" align="center">SGST%</TableCell>
                  <TableCell width="10%" align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {template.items_table.rows.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.description}
                        onChange={(e) => handleUpdateTableRow(index, 'description', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Select
                        size="small"
                        value={row.unit}
                        onChange={(e) => handleUpdateTableRow(index, 'unit', e.target.value)}
                        sx={{ width: 80 }}
                      >
                        <MenuItem value="pc">pc</MenuItem>
                        <MenuItem value="hr">hour</MenuItem>
                        <MenuItem value="day">day</MenuItem>
                        <MenuItem value="job">job</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={row.unit_price}
                        onChange={(e) => handleUpdateTableRow(index, 'unit_price', parseFloat(e.target.value))}
                        sx={{ width: 100 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={row.default_qty}
                        onChange={(e) => handleUpdateTableRow(index, 'default_qty', parseFloat(e.target.value))}
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={row.cgst}
                        onChange={(e) => handleUpdateTableRow(index, 'cgst', parseFloat(e.target.value))}
                        sx={{ width: 60 }}
                        InputProps={{ endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span> }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={row.sgst}
                        onChange={(e) => handleUpdateTableRow(index, 'sgst', parseFloat(e.target.value))}
                        sx={{ width: 60 }}
                        InputProps={{ endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span> }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTableRow(index)}
                        color="error"
                      >
                        <Remove />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button
            startIcon={<Add />}
            onClick={handleAddTableRow}
            sx={{ mt: 2 }}
          >
            Add Item Row
          </Button>
          
          {/* Table Styling */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Table Styling</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={template.items_table.show_header_color}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      items_table: { ...prev.items_table, show_header_color: e.target.checked }
                    }))}
                  />
                }
                label="Color Header"
              />
              
              {template.items_table.show_header_color && (
                <TextField
                  type="color"
                  value={template.items_table.header_color}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    items_table: { ...prev.items_table, header_color: e.target.value }
                  }))}
                  sx={{ width: 60 }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );

  const renderCostsSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachMoney /> Additional Costs
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={template.additional_costs.enabled}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                additional_costs: { ...prev.additional_costs, enabled: e.target.checked }
              }))}
            />
          }
          label="Enable Additional Costs"
        />
      </Box>
      
      {template.additional_costs.enabled && (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Cost Name</TableCell>
                  <TableCell align="center">Default Value</TableCell>
                  <TableCell align="center">Enabled by Default</TableCell>
               
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {template.additional_costs.costs.map((cost, index) => (
                  <TableRow key={cost.id} hover>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={cost.name}
                        onChange={(e) => handleUpdateAdditionalCost(index, 'name', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={cost.default_value}
                        onChange={(e) => handleUpdateAdditionalCost(index, 'default_value', parseFloat(e.target.value))}
                        sx={{ width: 100 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={cost.enabled_by_default}
                        onChange={(e) => handleUpdateAdditionalCost(index, 'enabled_by_default', e.target.checked)}
                      />
                    </TableCell>
               
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAdditionalCost(index)}
                        color="error"
                      >
                        <Remove />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button
            startIcon={<Add />}
            onClick={handleAddAdditionalCost}
            sx={{ mb: 3 }}
          >
            Add Additional Cost
          </Button>
          
          {/* Display Options */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Display Options</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={template.additional_costs.show_as_table}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    additional_costs: { ...prev.additional_costs, show_as_table: e.target.checked }
                  }))}
                />
              }
              label="Show as Table (vs List)"
            />
          </Box>
        </>
      )}
    </Box>
  );

const renderSummarySection = () => (
  <Box>
    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Receipt /> Summary Section
    </Typography>
    
    <Box sx={{ mb: 3 }}>
      <FormControlLabel
        control={
          <Switch
            checked={template.summary_section.enabled}
            onChange={(e) => setTemplate(prev => ({
              ...prev,
              summary_section: { ...prev.summary_section, enabled: e.target.checked }
            }))}
          />
        }
        label="Enable Summary Section"
      />
    </Box>
    
    {template.summary_section.enabled && (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>Summary Items to Show</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_items_total}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_items_total: e.target.checked }
                  }))}
                />
              }
              label="Items Total"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_additional_costs}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_additional_costs: e.target.checked }
                  }))}
                />
              }
              label="Additional Costs"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_subtotal}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_subtotal: e.target.checked }
                  }))}
                />
              }
              label="Subtotal"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_discount}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_discount: e.target.checked }
                  }))}
                />
              }
              label="Discount"
            />
            
            {template.summary_section.show_discount && (
              <TextField
                label="Default Discount %"
                type="number"
                size="small"
                value={template.summary_section.default_discount_percent}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  summary_section: { ...prev.summary_section, default_discount_percent: parseFloat(e.target.value) }
                }))}
                sx={{ ml: 4, width: 150 }}
                InputProps={{ endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span> }}
              />
            )}
          </Box>

          {/* ADDED: Advance Payment Options */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Advance Payment Options</Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_advance_payment}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_advance_payment: e.target.checked }
                  }))}
                />
              }
              label="Show Advance Payment Options"
            />
            
            {template.summary_section.show_advance_payment && (
              <Box sx={{ ml: 4, mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={template.summary_section.advance_payment_options.full_payment.enabled}
                      onChange={(e) => setTemplate(prev => ({
                        ...prev,
                        summary_section: {
                          ...prev.summary_section,
                          advance_payment_options: {
                            ...prev.summary_section.advance_payment_options,
                            full_payment: {
                              ...prev.summary_section.advance_payment_options.full_payment,
                              enabled: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Full Payment Option"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={template.summary_section.advance_payment_options.advance_payment.enabled}
                      onChange={(e) => setTemplate(prev => ({
                        ...prev,
                        summary_section: {
                          ...prev.summary_section,
                          advance_payment_options: {
                            ...prev.summary_section.advance_payment_options,
                            advance_payment: {
                              ...prev.summary_section.advance_payment_options.advance_payment,
                              enabled: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Advance Payment Option"
                />
                
                {template.summary_section.advance_payment_options.advance_payment.enabled && (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <TextField
                      label="Advance Percentage %"
                      type="number"
                      size="small"
                      value={template.summary_section.advance_payment_options.advance_payment.percentage}
                      onChange={(e) => setTemplate(prev => ({
                        ...prev,
                        summary_section: {
                          ...prev.summary_section,
                          advance_payment_options: {
                            ...prev.summary_section.advance_payment_options,
                            advance_payment: {
                              ...prev.summary_section.advance_payment_options.advance_payment,
                              percentage: parseFloat(e.target.value)
                            }
                          }
                        }
                      }))}
                      sx={{ width: 150 }}
                      InputProps={{ endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span> }}
                    />
                  </Box>
                )}
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={template.summary_section.advance_payment_options.custom_payment.enabled}
                      onChange={(e) => setTemplate(prev => ({
                        ...prev,
                        summary_section: {
                          ...prev.summary_section,
                          advance_payment_options: {
                            ...prev.summary_section.advance_payment_options,
                            custom_payment: {
                              ...prev.summary_section.advance_payment_options.custom_payment,
                              enabled: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Custom Payment Terms Option"
                />
              </Box>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>Tax Display & Layout</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_cgst_sgst_separate}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_cgst_sgst_separate: e.target.checked }
                  }))}
                />
              }
              label="Show CGST & SGST Separately"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_total_gst}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_total_gst: e.target.checked }
                  }))}
                />
              }
              label="Show Total GST"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_grand_total}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_grand_total: e.target.checked }
                  }))}
                />
              }
              label="Show Grand Total"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_notes_box}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_notes_box: e.target.checked }
                  }))}
                />
              }
              label="Show Notes Box"
            />
            
            {template.summary_section.show_notes_box && (
              <TextField
                label="Notes Box Content"
                multiline
                rows={3}
                value={template.summary_section.notes_content}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  summary_section: { ...prev.summary_section, notes_content: e.target.value }
                }))}
                sx={{ ml: 4 }}
              />
            )}

            {/* ADDED: Bank Details Section */}
            <FormControlLabel
              control={
                <Switch
                  checked={template.summary_section.show_bank_details}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: { ...prev.summary_section, show_bank_details: e.target.checked }
                  }))}
                />
              }
              label="Show Bank Details"
              sx={{ mt: 2 }}
            />
            
            {template.summary_section.show_bank_details && (
              <Box sx={{ ml: 4, mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Bank Account Details</Typography>
                
                <TextField
                  label="Bank Name"
                  size="small"
                  fullWidth
                  value={template.summary_section.bank_details.bank_name}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: {
                      ...prev.summary_section,
                      bank_details: {
                        ...prev.summary_section.bank_details,
                        bank_name: e.target.value
                      }
                    }
                  }))}
                  sx={{ mb: 1 }}
                />
                
                <TextField
                  label="Account Name"
                  size="small"
                  fullWidth
                  value={template.summary_section.bank_details.account_name}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: {
                      ...prev.summary_section,
                      bank_details: {
                        ...prev.summary_section.bank_details,
                        account_name: e.target.value
                      }
                    }
                  }))}
                  sx={{ mb: 1 }}
                />
                
                <TextField
                  label="Account Number"
                  size="small"
                  fullWidth
                  value={template.summary_section.bank_details.account_number}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: {
                      ...prev.summary_section,
                      bank_details: {
                        ...prev.summary_section.bank_details,
                        account_number: e.target.value
                      }
                    }
                  }))}
                  sx={{ mb: 1 }}
                />
                
                <TextField
                  label="IFSC Code"
                  size="small"
                  fullWidth
                  value={template.summary_section.bank_details.ifsc_code}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: {
                      ...prev.summary_section,
                      bank_details: {
                        ...prev.summary_section.bank_details,
                        ifsc_code: e.target.value
                      }
                    }
                  }))}
                  sx={{ mb: 1 }}
                />
                
                <TextField
                  label="Branch"
                  size="small"
                  fullWidth
                  value={template.summary_section.bank_details.branch}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    summary_section: {
                      ...prev.summary_section,
                      bank_details: {
                        ...prev.summary_section.bank_details,
                        branch: e.target.value
                      }
                    }
                  }))}
                />
              </Box>
            )}
          </Box>
        </Grid>
        
        {/* Preview of Summary Layout - UPDATED with Payment Options */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Summary Layout Preview</Typography>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {template.summary_section.show_items_total && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Items Total:</Typography>
                  <Typography variant="body2">{formatCurrency(previewTotals.itemsTotal)}</Typography>
                </Box>
              )}
              
              {template.summary_section.show_additional_costs && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Additional Costs:</Typography>
                  <Typography variant="body2">{formatCurrency(previewTotals.additionalCosts)}</Typography>
                </Box>
              )}
              
              {template.summary_section.show_subtotal && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Subtotal:</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatCurrency(previewTotals.subtotal)}</Typography>
                </Box>
              )}
              
              {template.summary_section.show_discount && template.summary_section.default_discount_percent > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                  <Typography variant="body2">Discount ({template.summary_section.default_discount_percent}%):</Typography>
                  <Typography variant="body2">-{formatCurrency(previewTotals.discountAmount)}</Typography>
                </Box>
              )}
              
              {template.summary_section.show_cgst_sgst_separate && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#e8f4fd', p: 1, borderRadius: 1 }}>
                    <Typography variant="body2">Total CGST:</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(previewTotals.cgstTotal)}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#e8f4fd', p: 1, borderRadius: 1 }}>
                    <Typography variant="body2">Total SGST:</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(previewTotals.sgstTotal)}</Typography>
                  </Box>
                </>
              )}
              
              {template.summary_section.show_total_gst && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f0f0f0', p: 1, borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Total GST:</Typography>
                  <Typography variant="body2" fontWeight="bold">{formatCurrency(previewTotals.gstTotal)}</Typography>
                </Box>
              )}
              
              {template.summary_section.show_grand_total && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.light', p: 2, borderRadius: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary">GRAND TOTAL</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">{formatCurrency(previewTotals.grandTotal)}</Typography>
                  </Box>
                </>
              )}

              {/* ADDED: Advance Payment Options Preview */}
              {template.summary_section.show_advance_payment && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Payment Options
                  </Typography>
                  
                  {template.summary_section.advance_payment_options.full_payment.enabled && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#e8f5e9', borderRadius: 1, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Full Payment:</strong> {template.summary_section.advance_payment_options.full_payment.description}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(previewTotals.grandTotal)}
                      </Typography>
                    </Box>
                  )}
                  
                  {template.summary_section.advance_payment_options.advance_payment.enabled && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#e8f4fd', borderRadius: 1, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Advance Payment ({template.summary_section.advance_payment_options.advance_payment.percentage}%):</strong> 
                        {template.summary_section.advance_payment_options.advance_payment.description}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(previewTotals.grandTotal * (template.summary_section.advance_payment_options.advance_payment.percentage / 100))}
                      </Typography>
                    </Box>
                  )}
                  
                  {template.summary_section.advance_payment_options.custom_payment.enabled && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Custom Payment:</strong> {template.summary_section.advance_payment_options.custom_payment.description}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* ADDED: Bank Details Preview */}
              {template.summary_section.show_bank_details && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Bank Account Details
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Bank:</strong> {template.summary_section.bank_details.bank_name}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Account Name:</strong> {template.summary_section.bank_details.account_name}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Account No.:</strong> {template.summary_section.bank_details.account_number}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>IFSC:</strong> {template.summary_section.bank_details.ifsc_code}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Branch:</strong> {template.summary_section.bank_details.branch}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Notes Box */}
              {template.summary_section.show_notes_box && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Notes
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 1, whiteSpace: 'pre-line' }}>
                    <Typography variant="body2">
                      {template.summary_section.notes_content}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    )}
  </Box>
);

  const renderTermsSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShipping /> Terms & Conditions
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={template.terms_section.enabled}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                terms_section: { ...prev.terms_section, enabled: e.target.checked }
              }))}
            />
          }
          label="Enable Terms & Conditions"
        />
      </Box>
      
      {template.terms_section.enabled && (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell width="20%">Label</TableCell>
                  <TableCell width="65%">Content</TableCell>
                  <TableCell width="10%" align="center">Visible</TableCell>
                  <TableCell width="5%" align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {template.terms_section.items.map((term, index) => (
                  <TableRow key={term.id} hover>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={term.label}
                        onChange={(e) => handleUpdateTerm(index, 'label', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={term.content}
                        onChange={(e) => handleUpdateTerm(index, 'content', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={term.visible}
                        onChange={(e) => handleUpdateTerm(index, 'visible', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTerm(index)}
                        color="error"
                      >
                        <Remove />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button
            startIcon={<Add />}
            onClick={handleAddTerm}
            sx={{ mb: 3 }}
          >
            Add Term
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, margin: '0 auto', p: 3 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
         
          Quotation Template Designer
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Design templates exactly like your PDF layout. Edit content directly in the template structure.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Panel - Template Navigation & Editor */}
        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {/* Template Name */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={template.name}
                  onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                  variant="outlined"
                  InputProps={{
                    sx: { fontSize: '1.5rem', fontWeight: 'bold' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Chip label={`Version ${template.version}`} size="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Section Navigation */}
              <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { id: 'header', label: 'Header', icon: <Title /> },
                  { id: 'details', label: 'Bill To & Details', icon: <Receipt /> },
                  { id: 'items', label: 'Items Table', icon: <ShoppingCart /> },
                  { id: 'costs', label: 'Additional Costs', icon: <AttachMoney /> },
                  { id: 'summary', label: 'Summary', icon: <Receipt /> },
                  { id: 'terms', label: 'Terms', icon: <LocalShipping /> },
                ].map((section) => (
                  <Button
                    key={section.id}
                    variant={activeSection === section.id ? "contained" : "outlined"}
                    startIcon={section.icon}
                    onClick={() => setActiveSection(section.id)}
                    size="small"
                  >
                    {section.label}
                  </Button>
                ))}
              </Box>

              {/* Section Editor */}
              {renderTemplateSection()}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {/* <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => {
                const newTemplate = { ...template, id: Date.now().toString(), name: `${template.name} (Copy)` };
                setTemplate(newTemplate);
                setSnackbar({ open: true, message: 'Template duplicated!', severity: 'info' });
              }}
            >
              Duplicate
            </Button> */}
            
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={generatePDFPreview}
            >
              PDF Preview
            </Button>

             <Button
              variant="contained"
              color="success"
              startIcon={<Launch />}
              onClick={handleUseTemplate}
              sx={{ 
                bgcolor: 'success.main',
                '&:hover': {
                  bgcolor: 'success.dark',
                }
              }}
            >
              Use Template
            </Button>
            
            {/* Save Template Button */}
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveTemplate}
            >
              Save Template
            </Button>
          </Box>
        </Grid>

  
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateDesigner;